import { IKpiAppUsage, KpiAppUsagePlatform } from '@/types/response';
import {
  Card,
  Empty,
  Segmented,
  Select,
  Table,
  TableColumnsType,
  Typography,
} from 'antd';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { SERIES_COLORS, formatNumber } from '../Dashboard/KpiChart';
import { IPeriod } from './appUsage';
import {
  IFunnelStep,
  IOtherEvent,
  TREND_METRICS,
  cohortValues,
  funnelSteps,
  labelOfCohort,
  otherEvents,
  previousWeekOf,
  retentions,
  weeklyTrend,
} from './funnel';

const { Title, Text } = Typography;

const PLATFORM_OPTIONS: { label: string; value: KpiAppUsagePlatform }[] = [
  { label: '合計', value: 'all' },
  { label: 'iOS', value: 'ios' },
  { label: 'Android', value: 'android' },
];

const DEFAULT_TREND = [
  'af_complete_registration',
  'diary_published',
  'family_connected',
];

const formatPct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);

/** 前の週との差（ポイント） */
const DiffText: React.FC<{ pt: number | null }> = ({ pt }) => {
  if (pt === null) return <Text type='secondary'>—</Text>;
  const color =
    pt > 0 ? 'text-[#1baf7a]' : pt < 0 ? 'text-[#d64545]' : 'text-fg-muted';

  return (
    <span className={color}>
      {pt > 0 ? '▲' : pt < 0 ? '▼' : '±'}
      {Math.abs(pt).toFixed(1)}pt
    </span>
  );
};

interface IProps {
  all: IKpiAppUsage[];
  period: IPeriod;
}

/** 新規ファネル（AppsFlyer のインストール週ごと）。インストールした人が7日以内にどこまで進んだか */
export const FunnelView: React.FC<IProps> = ({ all, period }) => {
  const [platform, setPlatform] = useState<KpiAppUsagePlatform>('all');
  const [trendNames, setTrendNames] = useState<string[]>(DEFAULT_TREND);

  const prevWeek = previousWeekOf(all, period);
  const values = useMemo(
    () => cohortValues(all, period, platform),
    [all, period, platform],
  );
  const prevValues = useMemo(
    () => (prevWeek ? cohortValues(all, prevWeek, platform) : null),
    [all, prevWeek, platform],
  );
  const steps = useMemo(
    () => funnelSteps(values, prevValues),
    [values, prevValues],
  );
  const retention = useMemo(
    () => retentions(values, prevValues),
    [values, prevValues],
  );
  const others = useMemo(() => otherEvents(values), [values]);
  const trend = useMemo(
    () => weeklyTrend(all, platform, trendNames),
    [all, platform, trendNames],
  );
  const installs = steps[0].users;

  const stepColumns: TableColumnsType<IFunnelStep> = [
    {
      title: 'ステップ',
      key: 'label',
      width: 220,
      render: (_, s) => (
        <div className='flex flex-col'>
          <span className='text-fg'>{s.label}</span>
          <span className='text-fg-muted text-[11px]'>{s.name}</span>
        </div>
      ),
    },
    {
      title: 'インストール比',
      key: 'bar',
      render: (_, s) => (
        <div className='flex items-center gap-sm'>
          <div className='h-[14px] flex-1 rounded-sm bg-[#F0F0F0]'>
            <div
              className='h-full rounded-sm'
              style={{
                width: `${Math.min(s.installPct ?? 0, 100)}%`,
                backgroundColor: SERIES_COLORS[0],
              }}
            />
          </div>
          <span className='w-[56px] text-right'>{formatPct(s.installPct)}</span>
        </div>
      ),
    },
    {
      title: '人数',
      dataIndex: 'users',
      align: 'right',
      width: 80,
      render: (v: number) => formatNumber(v),
    },
    {
      title: '前のステップ比',
      dataIndex: 'prevStepPct',
      align: 'right',
      width: 130,
      render: (v: number | null) => formatPct(v),
    },
    {
      title: '前週差',
      dataIndex: 'installPctDiff',
      align: 'right',
      width: 90,
      render: (v: number | null) => <DiffText pt={v} />,
    },
  ];

  const otherColumns: TableColumnsType<IOtherEvent> = [
    {
      title: '機能',
      key: 'label',
      render: (_, r) => (
        <div className='flex flex-col'>
          <span className='text-fg'>{r.label}</span>
          {r.label !== r.name && (
            <span className='text-fg-muted text-[11px]'>{r.name}</span>
          )}
        </div>
      ),
    },
    {
      title: '使った人',
      dataIndex: 'users',
      align: 'right',
      width: 100,
      render: (v: number) => formatNumber(v),
    },
    {
      title: 'インストール比',
      dataIndex: 'installPct',
      align: 'right',
      width: 120,
      render: (v: number | null) => formatPct(v),
    },
    {
      title: 'イベント数',
      dataIndex: 'count',
      align: 'right',
      width: 100,
      render: (v: number) => formatNumber(v),
    },
  ];

  return (
    <>
      <section className='flex flex-col gap-md'>
        <div className='flex flex-wrap items-center justify-between gap-md'>
          <div className='flex items-baseline gap-sm'>
            <Title level={5} className='!mb-0'>
              {period.label}
            </Title>
            <Text type='secondary' className='text-[12px]'>
              インストール後7日以内（AppsFlyer・UTC・端末単位）。
              {prevWeek
                ? `前週差は ${prevWeek.label} と比べたインストール比の差`
                : '前の週は未取り込み'}
            </Text>
          </div>
          <Segmented
            options={PLATFORM_OPTIONS}
            value={platform}
            onChange={(v) => setPlatform(v as KpiAppUsagePlatform)}
          />
        </div>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-md'>
          <Card size='small'>
            <div className='text-fg-muted text-[12px]'>インストール</div>
            <div className='text-fg text-[20px] font-semibold leading-tight'>
              {formatNumber(installs, '人')}
            </div>
          </Card>
          {retention.map((r) => (
            <Card key={r.name} size='small'>
              <div className='text-fg-muted text-[12px]'>{r.label}</div>
              <div className='flex items-baseline gap-sm'>
                <span className='text-fg text-[20px] font-semibold leading-tight'>
                  {formatPct(r.installPct)}
                </span>
                <span className='text-[12px]'>
                  <DiffText pt={r.installPctDiff} />
                </span>
              </div>
              <div className='text-fg-muted text-[11px]'>
                {formatNumber(r.users, '人')}
              </div>
            </Card>
          ))}
        </div>
      </section>

      <Card
        size='small'
        title='ファネル'
        extra={
          <Text type='secondary' className='text-[12px]'>
            各ステップは7日以内にそのイベントを起こした人数（前のステップを通ったかは問わない）
          </Text>
        }
      >
        <Table<IFunnelStep>
          size='small'
          rowKey='name'
          pagination={false}
          scroll={{ x: 760 }}
          dataSource={steps}
          columns={stepColumns}
        />
      </Card>

      <Card size='small' title='その他の機能（7日以内に使った人）'>
        <Table<IOtherEvent>
          size='small'
          rowKey='name'
          pagination={false}
          scroll={{ x: 560 }}
          dataSource={others}
          columns={otherColumns}
          locale={{ emptyText: 'ファネル以外の機能の取り込みはありません' }}
        />
      </Card>

      <Card
        size='small'
        title='週ごとの推移（インストールに対する割合）'
        extra={
          <Select
            mode='multiple'
            className='min-w-[320px] max-w-[560px]'
            value={trendNames}
            onChange={(v: string[]) =>
              setTrendNames(v.slice(0, SERIES_COLORS.length))
            }
            options={TREND_METRICS.map((m) => ({
              value: m.name,
              label: m.label,
            }))}
            optionFilterProp='label'
            maxTagCount='responsive'
            placeholder={`${SERIES_COLORS.length}つまで選べます`}
          />
        }
      >
        {trend.length === 0 ? (
          <Empty />
        ) : (
          <ResponsiveContainer width='100%' height={280}>
            <LineChart
              data={trend}
              margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
            >
              <CartesianGrid stroke='#E5E5E5' vertical={false} />
              <XAxis dataKey='week' tick={{ fontSize: 12, fill: '#666666' }} />
              <YAxis
                tick={{ fontSize: 12, fill: '#666666' }}
                tickFormatter={(v) => `${v}%`}
                width={48}
              />
              <Tooltip
                labelFormatter={(week) => `${week} の週にインストール`}
                formatter={(v: number, name: string) => [
                  `${v}%`,
                  labelOfCohort(name),
                ]}
              />
              <Legend formatter={(name: string) => labelOfCohort(name)} />
              {trendNames.map((name, i) => (
                <Line
                  key={name}
                  type='monotone'
                  dataKey={name}
                  stroke={SERIES_COLORS[i]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>
    </>
  );
};
